import { formatDate } from '@betterment/core';
import {
  addDays,
  addMonths,
  addYears,
  format,
  getDate,
  isFriday,
  isMonday,
  isThursday,
  isTuesday,
  isWednesday,
  parse,
  parseISO,
  setDate,
} from 'date-fns';

import { isTaxable as accountIsTaxable, isIRA, isRetirementIncome } from './betterment-account';

import { AutomaticTransactionFrequency, BettermentAccount, Weekday } from '../data';

export const ONCE_FREQUENCY = 'Once';
export const MONTHLY_FREQUENCY = 'Monthly';
export const TWICE_A_MONTH_FREQUENCY = 'Twice a month';
export const EVERY_OTHER_WEEK_FREQUENCY = 'Every other week';
export const WEEKLY_FREQUENCY = 'Weekly';
export const ANNUAL_FREQUENCY = 'Annually';

export const FREQUENCY_OPTIONS: Record<'value', AutomaticTransactionFrequency>[] = [
  { value: ONCE_FREQUENCY },
  { value: MONTHLY_FREQUENCY },
  { value: TWICE_A_MONTH_FREQUENCY },
  { value: EVERY_OTHER_WEEK_FREQUENCY },
  { value: WEEKLY_FREQUENCY },
  { value: ANNUAL_FREQUENCY },
];

export const frequencyOptions = (showOnce: boolean, showAnnual: boolean) => {
  let options = FREQUENCY_OPTIONS;
  options = showOnce ? options : options.slice(1);
  return showAnnual ? options : options.slice(0, -1);
};

export type Month =
  | 'January'
  | 'February'
  | 'March'
  | 'April'
  | 'May'
  | 'June'
  | 'July'
  | 'August'
  | 'September'
  | 'October'
  | 'November'
  | 'December';

export const MONTHLY_OPTIONS: Record<'value', Month>[] = [
  { value: 'January' },
  { value: 'February' },
  { value: 'March' },
  { value: 'April' },
  { value: 'May' },
  { value: 'June' },
  { value: 'July' },
  { value: 'August' },
  { value: 'September' },
  { value: 'October' },
  { value: 'November' },
  { value: 'December' },
];

export const DAY_OF_MONTH_OPTIONS = new Array(28).fill(0).map((_, index) => ({ value: index + 1 }));

export const DAY_OF_WEEK_OPTIONS: Record<'value', Weekday>[] = [
  { value: 'Monday' },
  { value: 'Tuesday' },
  { value: 'Wednesday' },
  { value: 'Thursday' },
  { value: 'Friday' },
];

export const getWeeklyOptions = (startDate: Date) => {
  const dateOptions = new Array(6).fill(0);
  return dateOptions.map((_, index) => {
    const date = addDays(startDate, 7 * index);
    return { value: formatDate(date, 'iso8601'), text: formatDate(date, 'shortOrdinalShortMonth') };
  });
};

export const getWeeklyStartDate = (values: { dayOfWeek: Weekday; showNextYearDates?: boolean }): Date => {
  const { dayOfWeek, showNextYearDates } = values;
  const nextYear = new Date().getFullYear() + 1;
  let startDate = showNextYearDates ? new Date(nextYear, 0, 1) : new Date();

  switch (dayOfWeek) {
    case 'Monday':
      while (!isMonday(startDate)) {
        startDate = addDays(startDate, 1);
      }
      return startDate;
    case 'Tuesday':
      while (!isTuesday(startDate)) {
        startDate = addDays(startDate, 1);
      }
      break;
    case 'Wednesday':
      while (!isWednesday(startDate)) {
        startDate = addDays(startDate, 1);
      }
      break;
    case 'Thursday':
      while (!isThursday(startDate)) {
        startDate = addDays(startDate, 1);
      }
      break;
    case 'Friday':
      while (!isFriday(startDate)) {
        startDate = addDays(startDate, 1);
      }
      break;
  }

  return startDate;
};

export const nextWeeklyOptions = (dayOfWeek: Weekday, showNextYearDates?: boolean) => {
  const startDate = getWeeklyStartDate({ dayOfWeek, showNextYearDates });
  return getWeeklyOptions(startDate);
};

export function nth(n: number) {
  return format(setDate(new Date(), n), 'do');
}

interface AutomaticTransactionValues {
  frequency: AutomaticTransactionFrequency;
  month?: Month;
  monthlySelection1?: number;
  monthlySelection2?: number;
  dayOfMonth?: number;
  weeklyStartOn?: string;
  dayOfWeekSelection?: Weekday;
  isTaxYearNextYear?: boolean;
}

export function frequencyCopy(values: AutomaticTransactionValues) {
  const { frequency, dayOfWeekSelection, month, monthlySelection1, monthlySelection2, dayOfMonth } = values;
  switch (frequency) {
    case MONTHLY_FREQUENCY:
      return `${nth(monthlySelection1!)} of each month`;
    case TWICE_A_MONTH_FREQUENCY:
      return `${nth(monthlySelection1!)} and ${nth(monthlySelection2!)} of each month`;
    case WEEKLY_FREQUENCY:
      return `Weekly on ${String(dayOfWeekSelection)}s`;
    case EVERY_OTHER_WEEK_FREQUENCY:
      return `Every other ${String(dayOfWeekSelection)}`;
    case ANNUAL_FREQUENCY:
      return `Annually on ${String(month)} ${String(dayOfMonth)}`;
    default:
      return null;
  }
}

export const getStartingOnDate = (values: AutomaticTransactionValues) => {
  const { isTaxYearNextYear, frequency, month, monthlySelection1, monthlySelection2, dayOfMonth, weeklyStartOn } =
    values;
  if ((frequency === WEEKLY_FREQUENCY || frequency === EVERY_OTHER_WEEK_FREQUENCY) && weeklyStartOn) {
    return parseISO(weeklyStartOn);
  }

  const nextYear = new Date().getFullYear() + 1;
  const firstPossibleDate = isTaxYearNextYear ? new Date(nextYear, 0, 1) : new Date();

  if (frequency === MONTHLY_FREQUENCY && monthlySelection1) {
    if (getDate(firstPossibleDate) > monthlySelection1) {
      return setDate(addMonths(firstPossibleDate, 1), monthlySelection1);
    }
    return setDate(firstPossibleDate, monthlySelection1);
  }

  if (frequency === TWICE_A_MONTH_FREQUENCY && monthlySelection1 && monthlySelection2) {
    if (getDate(firstPossibleDate) > monthlySelection1 && getDate(firstPossibleDate) > monthlySelection2) {
      return setDate(addMonths(firstPossibleDate, 1), monthlySelection1);
    }
    if (getDate(firstPossibleDate) < monthlySelection1) {
      return setDate(firstPossibleDate, monthlySelection1);
    }
    return setDate(firstPossibleDate, monthlySelection2);
  }

  if (frequency === ANNUAL_FREQUENCY && month && dayOfMonth) {
    const annualDate = parse(`${month} ${dayOfMonth}`, 'LLLL d', firstPossibleDate);
    return annualDate < firstPossibleDate ? addYears(annualDate, 1) : annualDate;
  }

  return null as never;
};

export function startOnCopy(values: AutomaticTransactionValues) {
  const startingOnDate = getStartingOnDate(values);
  return format(startingOnDate, 'eeee, MMMM do');
}

export const determineAnnualDateFields = (annualDate?: string | null): [Month, number] => {
  annualDate = annualDate ?? new Date().toISOString();
  if (formatDate(parseISO(annualDate), 'shortOrdinalShortMonth') === 'Feb 29') {
    return ['March', 1];
  }
  const parsedAnnualDate = parseISO(annualDate);
  return [formatDate(parsedAnnualDate, 'month') as Month, getDate(parsedAnnualDate)];
};

type RequiredValues = {
  frequency?: AutomaticTransactionFrequency;
  withdrawalType?: string;
};
export const shouldDisplayAutomaticTransactionFormOnSelectionStep = <Values extends RequiredValues>(
  values: Values,
  sourceAccount?: BettermentAccount
): sourceAccount is BettermentAccount => {
  if (values.frequency === undefined) return false;
  if (values.withdrawalType === 'recurring' && !isIRA(sourceAccount)) return true;
  if (isRetirementIncome(sourceAccount) && accountIsTaxable(sourceAccount)) return true;
  return false;
};
